/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { ticketDeskTable } from '../../shared/ticket/ticketDeskTable.styles';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';

import { formatMoney } from '../../shared/format/public-api';
export type PurchaseDraftLine = {
  id: string;
  productId: string;
  productName: string;
  productSku: string | null;
  quantity: string;
  unitCost: string;
  unitOfMeasure: string | null;
  targetBranchId: string | null;
  /** #713: variante de destino (null = parent). */
  variantId: string | null;
  /** #713: label de la variante para mostrar en la línea. */
  variantName: string | null;
};

type Props = {
  items: PurchaseDraftLine[];
  isMobile: boolean;
  submitting: boolean;
  branchNamesById?: Record<string, string>;
  onChange: (id: string, field: 'quantity' | 'unitCost', value: string) => void;
  onRemove: (id: string) => void;
};


function lineSubtotal(item: PurchaseDraftLine): number {
  const qty = parseDecimalInput(item.quantity) ?? 0;
  const cost = parseDecimalInput(item.unitCost) ?? 0;
  return qty * cost;
}

export function PurchaseDraftLineList({
  items,
  isMobile,
  submitting,
  branchNamesById,
  onChange,
  onRemove,
}: Props) {
  if (items.length === 0) {
    return (
      <Text style={styles.empty}>
        Todavía no hay productos. Buscá uno, poné cantidad y costo, y dale Agregar.
      </Text>
    );
  }

  if (isMobile) {
    return (
      <View style={styles.mobileList}>
        {items.map((item) => {
          const dest =
            item.targetBranchId && branchNamesById?.[item.targetBranchId]
              ? ` · ${branchNamesById[item.targetBranchId]}`
              : '';
          const variantSuffix = item.variantName ? ` · ${item.variantName}` : '';
          return (
            <View key={item.id} style={styles.mobileCard}>
              <View style={styles.mobileCardHead}>
                <View style={styles.flex1}>
                  <Text style={styles.mobileTitle} numberOfLines={2}>
                    {item.productName}
                  </Text>
                  <Text style={styles.mobileSub} numberOfLines={1}>
                    {`${item.productSku ? `${item.productSku} · ` : ''}${formatMoney(lineSubtotal(item))}${variantSuffix}${dest}`}
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.removeBtn}
                  onPress={() => onRemove(item.id)}
                  disabled={submitting}
                  accessibilityRole="button"
                  accessibilityLabel={`Quitar ${item.productName}`}
                >
                  <Text style={styles.removeBtnText}>Quitar</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.mobileFooter}>
                <View style={styles.mobileField}>
                  <Text style={styles.mobileLabel}>Cant.</Text>
                  <NumericTextInput
                    kind="decimalMoney"
                    value={item.quantity}
                    onChangeText={(v) => onChange(item.id, 'quantity', v)}
                    placeholder="0"
                    style={[modalExperience.formInput, styles.compactInput]}
                    placeholderTextColor={ticketColors.ink3}
                    editable={!submitting}
                  />
                </View>
                <View style={styles.mobileField}>
                  <Text style={styles.mobileLabel}>Costo</Text>
                  <NumericTextInput
                    kind="decimalMoney"
                    value={item.unitCost}
                    onChangeText={(v) => onChange(item.id, 'unitCost', v)}
                    placeholder="0"
                    style={[modalExperience.formInput, styles.compactInput]}
                    placeholderTextColor={ticketColors.ink3}
                    editable={!submitting}
                  />
                </View>
              </View>
            </View>
          );
        })}
      </View>
    );
  }

  return (
    <View style={ticketDeskTable.tableOuter}>
      <View style={ticketDeskTable.table}>
        <View style={[ticketDeskTable.row, ticketDeskTable.headerRow]} pointerEvents="none">
          <Text style={[ticketDeskTable.headerCell, styles.colProduct]}>Producto</Text>
          <Text style={[ticketDeskTable.headerCell, styles.colNum]}>Cant.</Text>
          <Text style={[ticketDeskTable.headerCell, styles.colNum]}>Costo</Text>
          <Text style={[ticketDeskTable.headerCell, styles.colMoney]}>Subtotal</Text>
          <Text style={[ticketDeskTable.headerCell, styles.colAction]} />
        </View>
        {items.map((item) => {
          const dest =
            item.targetBranchId && branchNamesById?.[item.targetBranchId]
              ? branchNamesById[item.targetBranchId]
              : null;
          const variantName = item.variantName ?? null;
          return (
            <View key={item.id} style={ticketDeskTable.row}>
              <View style={styles.colProduct}>
                <Text style={ticketDeskTable.cell} numberOfLines={1}>
                  {item.productName}
                </Text>
                <Text style={ticketDeskTable.cellMuted} numberOfLines={1}>
                  {[item.productSku, variantName, dest].filter(Boolean).join(' · ') || ' '}
                </Text>
              </View>
              <NumericTextInput
                kind="decimalMoney"
                value={item.quantity}
                onChangeText={(v) => onChange(item.id, 'quantity', v)}
                placeholder="0"
                style={[modalExperience.formInput, styles.compactInput, styles.colNum]}
                placeholderTextColor={ticketColors.ink3}
                editable={!submitting}
                accessibilityLabel={`Cantidad de ${item.productName}`}
              />
              <NumericTextInput
                kind="decimalMoney"
                value={item.unitCost}
                onChangeText={(v) => onChange(item.id, 'unitCost', v)}
                placeholder="0"
                style={[modalExperience.formInput, styles.compactInput, styles.colNum]}
                placeholderTextColor={ticketColors.ink3}
                editable={!submitting}
                accessibilityLabel={`Costo de ${item.productName}`}
              />
              <Text style={[ticketDeskTable.moneyText, styles.colMoney]}>
                {formatMoney(lineSubtotal(item))}
              </Text>
              <TouchableOpacity
                style={styles.removeBtn}
                onPress={() => onRemove(item.id)}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel={`Quitar ${item.productName}`}
              >
                <Text style={styles.removeBtnText}>Quitar</Text>
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  empty: {
    color: ticketColors.ink3,
    fontSize: 13,
    lineHeight: 18,
    paddingVertical: spacing.sm,
  },
  mobileList: {
    gap: 8,
  },
  mobileCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: 12,
    gap: 10,
  },
  mobileCardHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  flex1: {
    flex: 1,
    minWidth: 0,
  },
  mobileTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  mobileSub: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 2,
    fontFamily: ticketFonts.sans,
  },
  mobileFooter: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
  },
  mobileField: {
    flex: 1,
    minWidth: 0,
  },
  mobileLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink3,
    marginBottom: 4,
  },
  compactInput: {
    paddingVertical: 8,
    fontSize: 14,
    minHeight: 40,
  },
  colProduct: {
    flex: 1.6,
    minWidth: 140,
  },
  colNum: {
    width: 92,
    flexGrow: 0,
    flexShrink: 0,
    textAlign: 'right',
  },
  colMoney: {
    width: 100,
    flexGrow: 0,
    flexShrink: 0,
  },
  colAction: {
    width: 64,
    flexGrow: 0,
    flexShrink: 0,
  },
  removeBtn: {
    minHeight: 40,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  removeBtnText: {
    color: ticketColors.bad,
    fontSize: 13,
    fontWeight: '700',
  },
});
